import { useReducer } from 'react';
import dayjs from 'dayjs';

const Modes = ['range', 'expression'] as const;
type Mode = (typeof Modes)[number];

type DateRange = {
  from: Date | undefined;
  to?: Date | undefined;
};

type RangeState = { mode: 'range'; value: DateRange };
type ExpressionState = { mode: 'expression'; value: string };

type State = RangeState | ExpressionState;

type Action =
  | { type: 'SET_MODE'; mode: Mode }
  | { type: 'SET_RANGE'; value: DateRange }
  | { type: 'SET_EXPRESSION'; value: string };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_RANGE':
      return {
        ...state,
        mode: 'range',
        value: action.value,
      };

    case 'SET_EXPRESSION':
      return {
        ...state,
        mode: 'expression',
        value: action.value,
      };

    default:
      return state;
  }
}

export function extractDays(expression: string): string {
  if (!expression.includes('days')) {
    return '';
  }

  const match = expression.match(/\d+/);
  return match ? parseInt(match[0], 10).toString() : '';
}

function isExpression(state: State): state is ExpressionState {
  return state.mode === 'expression';
}

function isRange(state: State): state is RangeState {
  return state.mode === 'range';
}

function getInitialState(
  defaultValue: DateRange | string | undefined | null,
): State {
  if (!defaultValue) {
    return {
      mode: 'range',
      value: { from: undefined, to: undefined },
    };
  }

  return typeof defaultValue === 'string'
    ? { mode: 'expression', value: defaultValue }
    : {
        mode: 'range',
        value: {
          from: defaultValue?.from
            ? dayjs(defaultValue?.from).toDate()
            : undefined,
          to: defaultValue?.to ? dayjs(defaultValue?.to).toDate() : undefined,
        },
      };
}

type UseRelativePickerStateResultBase = {
  mode: Mode;
  setRange: (from: Date | undefined, to: Date | undefined) => void;
  setExpression: (expression: string) => void;
  setCustomDays: (mode: 'last' | 'next', days: number) => void;
  getSelectedRange: () => {
    readonly from: Date | undefined;
    readonly to?: Date | undefined;
  };
};

type UseRelativePickerStateResultRange = UseRelativePickerStateResultBase & {
  isExpression: false;
  isRange: true;
  value: DateRange;
  period: string;
};

type UseRelativePickerStateResultExpression =
  UseRelativePickerStateResultBase & {
    isExpression: true;
    isRange: false;
    value: string;
    period: string;
  };

type UseRelativePickerStateResult =
  | UseRelativePickerStateResultRange
  | UseRelativePickerStateResultExpression;

export function useRelativePickerState(
  defaultValue: DateRange | string | null | undefined,
): UseRelativePickerStateResult {
  const [state, dispatch] = useReducer(reducer, getInitialState(defaultValue));

  const setRange = (from: Date | undefined, to: Date | undefined) =>
    dispatch({
      type: 'SET_RANGE',
      value: {
        from: dayjs(from).startOf('day').toDate(),
        to: dayjs(to).endOf('day').toDate(),
      },
    });

  const setExpression = (expression: string) =>
    dispatch({ type: 'SET_EXPRESSION', value: expression });

  const getSelectedRange = () => {
    if (state.mode !== 'range') {
      const now = dayjs();
      const currentQuarter = Math.floor(now.month() / 3) + 1; // 1-based: 1 = Q1, 2 = Q2, etc.

      // Last quarter
      const lastQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1;
      const lastQuarterYear =
        currentQuarter === 1 ? now.year() - 1 : now.year();
      const lastQuarterStartMonth = (lastQuarter - 1) * 3;

      // Current quarter
      const currentQuarterStartMonth = (currentQuarter - 1) * 3;

      // Next quarter
      const nextQuarter = currentQuarter === 4 ? 1 : currentQuarter + 1;
      const nextQuarterYear =
        currentQuarter === 4 ? now.year() + 1 : now.year();
      const nextQuarterStartMonth = (nextQuarter - 1) * 3;

      const expressionMap = {
        last_week: {
          from: dayjs().subtract(1, 'week').startOf('week').toDate(),
          to: dayjs().subtract(1, 'week').endOf('week').toDate(),
        },
        last_month: {
          from: dayjs().subtract(1, 'month').startOf('month').toDate(),
          to: dayjs().subtract(1, 'month').endOf('month').toDate(),
        },
        last_quarter: {
          from: dayjs(new Date(lastQuarterYear, lastQuarterStartMonth, 1))
            .startOf('month')
            .toDate(),
          to: dayjs(new Date(lastQuarterYear, lastQuarterStartMonth + 2, 1))
            .endOf('month')
            .toDate(),
        },

        current_week: {
          from: dayjs().startOf('week').toDate(),
          to: dayjs().endOf('week').toDate(),
        },
        current_month: {
          from: dayjs().startOf('month').toDate(),
          to: dayjs().endOf('month').toDate(),
        },
        current_quarter: {
          from: dayjs(new Date(now.year(), currentQuarterStartMonth, 1))
            .startOf('month')
            .toDate(),
          to: dayjs(new Date(now.year(), currentQuarterStartMonth + 2, 1))
            .endOf('month')
            .toDate(),
        },

        next_week: {
          from: dayjs().add(1, 'week').startOf('week').toDate(),
          to: dayjs().add(1, 'week').endOf('week').toDate(),
        },
        next_month: {
          from: dayjs().add(1, 'month').startOf('month').toDate(),
          to: dayjs().add(1, 'month').endOf('month').toDate(),
        },
        next_quarter: {
          from: dayjs(new Date(nextQuarterYear, nextQuarterStartMonth, 1))
            .startOf('month')
            .toDate(),
          to: dayjs(new Date(nextQuarterYear, nextQuarterStartMonth + 2, 1))
            .endOf('month')
            .toDate(),
        },
      } as const;

      if (state.value in expressionMap) {
        return expressionMap[state.value as keyof typeof expressionMap];
      }

      const fromSpec = resolveDateRange(state.value);

      if (fromSpec) {
        return fromSpec;
      }

      return {
        from: undefined,
        to: undefined,
      };
    }

    return {
      from: state.value.from,
      to: state.value.to,
    };
  };

  const setCustomDays = (mode: 'last' | 'next', days: number) => {
    setExpression(`${mode}_${days || ''}_days`);
  };

  if (isRange(state)) {
    return {
      isExpression: false,
      isRange: true,
      mode: state.mode,
      value: state.value as DateRange,
      setRange,
      period: '',
      setExpression,
      setCustomDays,
      getSelectedRange,
    };
  } else {
    return {
      isExpression: true,
      isRange: false,
      mode: state.mode,
      value: state.value as string,
      setRange,
      period: extractDays(state.value),
      setExpression,
      setCustomDays,
      getSelectedRange,
    };
  }
}

function resolveDateRange(spec: string, now = dayjs()): DateRange | null {
  const match = spec.match(/^(last|next)_(\d+)_days$/);

  if (match) {
    const direction = match[1];
    const days = parseInt(match[2], 10);

    if (direction === 'last') {
      return {
        from: now.subtract(days, 'day').startOf('day').toDate(),
        to: now.endOf('day').toDate(),
      };
    }

    if (direction === 'next') {
      return {
        from: now.add(1, 'day').startOf('day').toDate(),
        to: now.add(days, 'day').endOf('day').toDate(),
      };
    }
  }

  return null;
}
